import { ref, computed } from 'vue';

import { defineStore } from 'pinia';

import { useMessagesMcb } from '@/composables/domains/chat/useMessagesMcb';
import useWebSocketUrl from '@/composables/shared/useWebSocketUrl';
import { getNormalizedLanguageCode } from '@/static/languages';
import { useCallStore } from '@/stores/call';
import { useMicrophoneStore } from '@/stores/microphone';
import { useUserStore } from '@/stores/user';
import {
  ConnectionInfo,
  IMessageCn,
  IMessageMf,
  IMessageVc,
  IMessageVd,
  IMessageVr,
  MessageFlag,
  ConnectionId,
  ConnectionEntry,
} from '@/types/socket';
import convertASCII from '@/utils/convertASCII';

export const useSocketsStore = defineStore('sockets', () => {
  const callStore = useCallStore();
  const userStore = useUserStore();
  const microphoneStore = useMicrophoneStore();

  const { requestWebsocketUrl, webSocketUrl } = useWebSocketUrl();

  // ---- URL resolver (same behavior as original) ----
  const connections = ref<Map<ConnectionId, ConnectionEntry>>(new Map());
  const listCardConnection = ref(
    JSON.parse(localStorage.getItem(`mcs-cardlist-${userStore.userInfo?.userId ?? ''}`) || '[]')
  ); // 저장된 MCS 소켓 연결 목록

  // MCS 사용자 아이디 목록
  const listUserMcs = computed<string[]>(() => {
    return [...connections.value.values()].map((v) => v.connectInfo?.userId || '');
  });

  const getOrCreate = (id: ConnectionId, userId?: string): ConnectionEntry => {
    if (!connections.value.has(id)) {
      connections.value.set(
        id,
        useMessagesMcb({
          userId,
          state: 'CLOSED',
          exceededUsageLimit: false,
          evicted: false,
          selectedChatId: id,
          connectionResponse: -1,
        })
      );
    }
    return connections.value.get(id);
  };

  const buildPayload = (flagStr: string, payloadObjOrStr: any = '') => {
    const flag = new TextEncoder().encode(flagStr);
    const payloadStr = typeof payloadObjOrStr === 'string' ? payloadObjOrStr : JSON.stringify(payloadObjOrStr);
    const message = new TextEncoder().encode(payloadStr);
    const messageLength = message.byteLength.toString().padStart(8, '0');
    return new Uint8Array([...flag, ...new TextEncoder().encode(messageLength), ...message]);
  };

  // ---- Connect info ----
  const setConnectInfo = (connectionId: ConnectionId, info: ConnectionInfo) => {
    const entry = getOrCreate(connectionId, info.userId);
    info.languageCode = getNormalizedLanguageCode(info.languageCode);
    entry.connectInfo = info;
    const qrCodeType = callStore.qrCodeInfo.qrCodeType;
    if (!entry.channel) {
      entry.channel = new BroadcastChannel(`connect_ws_${connectionId}`);
      entry.channel.postMessage({
        type: 'connect_ws',
        value: entry.selectedChatId + '|' + qrCodeType,
      });
    }
  };

  const setupWebSocket = async (connectionId: ConnectionId, forceReconnection = false) => {
    const entry = getOrCreate(connectionId);
    if (!entry.connectInfo) {
      console.error(`[${connectionId}] connectInfo is undefined`);
      return Promise.reject(new Error('no connectInfo'));
    }

    if (entry.ws && entry.ws.readyState === WebSocket.OPEN && !forceReconnection) {
      return; // 이미 OPEN
    }
    if (entry.ws && entry.ws.readyState !== WebSocket.CLOSED) {
      entry.ws.close();
    }

    entry.state = 'CONNECTING';
    entry.connectionResponse = 0;
    await requestWebsocketUrl(entry.connectInfo);
    const ws = new WebSocket(webSocketUrl.value);
    entry.ws = ws;

    // onopen을 Promise로 래핑
    const opened = new Promise<void>((resolve, reject) => {
      ws.onopen = () => {
        entry.connectionResponse = 1;
        entry.state = 'OPEN';

        if (entry.connectInfo) sendMessageConnect(connectionId);

        entry.pingTimeout = setTimeout(() => {
          sendMessagePi(connectionId);
          entry.pingInterval = setInterval(() => sendMessagePi(connectionId), 30000);
        }, 5000);
        resolve();
      };
      ws.onerror = (error) => {
        entry.connectionResponse = 9999;
        if (entry.state !== 'RETRY_FAILED' && entry.state !== 'CLOSED') entry.state = 'CLOSED';
        clearTimers(entry);
        reject(error as any);
      };
      ws.onmessage = (event) => {
        if (!entry.connectInfo) return;
        if (['GENERAL', 'VOICE-STREAMING'].includes(entry.connectInfo.connectionType)) {
          entry.handleWebSocketMessage?.(event.data);
        }
      };
      ws.onclose = () => {
        if (entry.state !== 'RETRY_FAILED' && entry.state !== 'CLOSED') entry.state = 'CLOSED';
        clearTimers(entry);
      };
    });

    return opened;
  };

  const clearTimers = (entry: ConnectionEntry) => {
    if (entry.pingInterval) {
      clearInterval(entry.pingInterval);
      entry.pingInterval = null;
    }
    if (entry.pingTimeout) {
      clearTimeout(entry.pingTimeout);
      entry.pingTimeout = null;
    }
  };

  // ---- Low-level send ----
  const sendRaw = (connectionId: ConnectionId, payload: ArrayBufferView | string) => {
    const entry = getOrCreate(connectionId);
    if (entry.ws && entry.ws.readyState === WebSocket.CONNECTING) {
      setTimeout(() => sendRaw(connectionId, payload), 500);
      return;
    }
    if (entry.ws && entry.ws.readyState === WebSocket.OPEN) {
      entry.ws.send(payload as any);
    } else {
      console.error(`[${connectionId}] WebSocket not open:`, entry.ws ? entry.ws.readyState : 'null');
    }
  };

  // ---- Broadcast ----
  const broadcast = (
    payload: ArrayBufferView | string,
    filter?: (id: ConnectionId, entry: ConnectionEntry) => boolean
  ) => {
    for (const [id, entry] of connections.value) {
      if (entry.ws?.readyState === WebSocket.OPEN && (!filter || filter(id, entry))) {
        entry.ws.send(payload as any);
      }
    }
  };

  // ---- Commands (ALL require connectionId) ----
  const sendMessageConnect = (connectionId: ConnectionId) => {
    const entry = getOrCreate(connectionId);
    if (!entry.connectInfo) return;
    sendRaw(connectionId, buildPayload('cc', entry.connectInfo));
  };

  const sendMessageDisconnect = (connectionId: ConnectionId) => {
    const entry = getOrCreate(connectionId);
    if (!entry.connectInfo) return;
    const payload = buildPayload('cd', {
      userId: entry.connectInfo.userId,
      chatId: entry.selectedChatId,
    });
    sendRaw(connectionId, payload);
  };

  const sendMessageTss = (connectionId: ConnectionId) => {
    const payload = buildPayload('tt', {
      isTssOn: microphoneStore.isTssOn ? '1' : '0',
    });
    sendRaw(connectionId, payload);
  };

  const sendMessageMuteGuests = (connectionId: ConnectionId, allowMicOn: boolean) => {
    const payload = buildPayload('am', {
      isMuteOn: allowMicOn ? '1' : '0',
      senderId: localStorage.getItem('userIdConnected'),
    });
    sendRaw(connectionId, payload);
    getOrCreate(connectionId).isGuestMicMuted = allowMicOn;
  };

  const sendMessageFi = (connectionId: ConnectionId, fastInterpretationOn: boolean) => {
    const payload = buildPayload('fi', {
      isFastInterpretationOn: fastInterpretationOn ? '1' : '0',
    });
    sendRaw(connectionId, payload);
    getOrCreate(connectionId).isFastInterpretation = fastInterpretationOn;
  };

  const sendMessageAttendees = (connectionId: ConnectionId) => sendRaw(connectionId, buildPayload('al'));

  const sendMessageEvictUser = (connectionId: ConnectionId, userId: string, chatId: string) =>
    sendRaw(connectionId, buildPayload('ev', { userId, chatId }));

  const sendMessageChangeDevice = (connectionId: ConnectionId, voiceId?: string) => {
    const v = voiceId ?? '0';
    const longVoice = ['CONSULTATION_CALL', 'GENERAL_CALL_PARTNER', 'CONFERENCE_CALL_PARTNER'].includes(
      callStore.qrCodeInfo.qrCodeType
    );
    sendRaw(connectionId, buildPayload('dv', { voiceId: v, longVoice }));
  };

  const sendMessageChangeLanguage = (connectionId: ConnectionId, languageCode: string, languageCodeAway?: string) => {
    if (typeof languageCode !== 'string') {
      console.error(`[${connectionId}] Invalid languageCode:`, languageCode);
      return false;
    }
    const lc = getNormalizedLanguageCode(languageCode);
    const lca = languageCodeAway ? getNormalizedLanguageCode(languageCodeAway) : (null as any);
    try {
      sendRaw(connectionId, buildPayload('cl', { languageCode: lc, languageCodeAway: lca }));
      localStorage.setItem('language', lc);
      return true;
    } catch {
      return false;
    }
  };

  const dispatchMessage = <T>(connectionId: ConnectionId, flag: MessageFlag, message: T) => {
    sendRaw(connectionId, buildPayload(flag, message));
  };

  const sendMessageAudioStreamStart = (connectionId: ConnectionId) => sendRaw(connectionId, buildPayload('ah'));
  const sendMessageAudioStreamEnd = (connectionId: ConnectionId) => sendRaw(connectionId, buildPayload('ac'));

  const sendMessageVoiceStreamStart = (connectionId: ConnectionId, message: IMessageVr) =>
    sendRaw(connectionId, buildPayload('vr', message));
  const sendMessageVoiceStreamEnd = (connectionId: ConnectionId, message: IMessageVc) =>
    sendRaw(connectionId, buildPayload('vc', message));

  const sendMessageText = (connectionId: ConnectionId, message: IMessageMf) =>
    sendRaw(connectionId, buildPayload('mf', message));

  const sendMessageVAD = (connectionId: ConnectionId, message: IMessageVd) =>
    sendRaw(connectionId, buildPayload('vd', message));

  const sendMessageCn = (connectionId: ConnectionId, message: IMessageCn) =>
    sendRaw(connectionId, buildPayload('cn', message));

  // ping
  const sendMessagePi = (connectionId: ConnectionId) => {
    sendRaw(connectionId, buildPayload('pi'));
  };

  const sendMessageAudioStreamChunk = (
    connectionId: ConnectionId,
    audioBytes: Uint8Array,
    messageFlag: MessageFlag
  ) => {
    const entry = getOrCreate(connectionId);
    if (entry.ws?.readyState !== WebSocket.OPEN) {
      console.error(`[${connectionId}] WebSocket is not open. Current state:`, entry.ws?.readyState);
      entry.state = 'CLOSED';
      return;
    }
    entry.streamingCheckCount += 1;
    const flag = convertASCII(messageFlag);
    const messageLength = audioBytes.byteLength.toString().padStart(8, '0');
    const payload = new Uint8Array([...flag, ...new TextEncoder().encode(messageLength), ...audioBytes]);
    sendRaw(connectionId, payload);
  };

  const close = (connectionId: ConnectionId, isForce: boolean) => {
    const entry = getOrCreate(connectionId);
    const err = new Error();
    const stackLines = err.stack?.split('').slice(4);
    const reason = (stackLines?.slice(0, 2).join(' | ') || 'No stack trace').slice(0, 123);
    if (entry.ws && entry.ws.readyState !== WebSocket.CLOSED) {
      entry.eventSocketClose = { code: 1000, reason } as any;
      entry.ws.close();
    }
    if (entry.channel) {
      entry.channel.close();
      entry.channel = undefined;
    }
    if (isForce && connections.value.has(connectionId)) {
      connections.value.delete(connectionId);
    }
  };

  const closeAll = () => {
    for (const [, entry] of connections.value) {
      if (entry.ws && entry.ws.readyState !== WebSocket.CLOSED) {
        entry.ws.close();
      }
    }
  };

  // 연결된 mcs 소켓 목록 저장
  const setListCardConnection = (newVal) => {
    listCardConnection.value = newVal;
    localStorage.setItem(`mcs-cardlist-${userStore.userInfo.userId}`, JSON.stringify(newVal));
  };

  return {
    connections,
    listUserMcs,
    listCardConnection,
    setConnectInfo,
    setupWebSocket,

    sendRaw,
    broadcast,

    dispatchMessage,
    sendMessageConnect,
    sendMessageDisconnect,
    sendMessageTss,
    sendMessageMuteGuests,
    sendMessageFi,
    sendMessageAttendees,
    sendMessageEvictUser,
    sendMessageChangeDevice,
    sendMessageChangeLanguage,
    sendMessageAudioStreamStart,
    sendMessageAudioStreamEnd,
    sendMessageVoiceStreamStart,
    sendMessageVoiceStreamEnd,
    sendMessageText,
    sendMessageVAD,
    sendMessageAudioStreamChunk,
    sendMessageCn,

    close,
    closeAll,

    setListCardConnection,
  };
});
